import * as React from 'react'

import { CheckboxWithLabel } from '@/components/lemmala-design-system/checkbox/checkbox'

export interface CheckboxOption {
  label: React.ReactNode
  value: string
  disabled?: boolean
  indeterminate?: boolean
}

export interface CheckboxGroupProps {
  label?: React.ReactNode
  direction?: 'vertical' | 'horizontal'
  options: CheckboxOption[]
  value?: string[]
  defaultValue?: string[]
  onChange?: (value: string[]) => void
  className?: string
  disabled?: boolean
}

export const CheckboxGroup: React.FC<CheckboxGroupProps> = ({
  label,
  direction = 'vertical',
  options,
  value,
  defaultValue,
  onChange,
  className = '',
  disabled = false,
}) => {
  const isControlled = value !== undefined
  const [internalValue, setInternalValue] = React.useState<string[]>(defaultValue || [])
  const currentValue = isControlled ? value! : internalValue

  const handleChange = (optionValue: string, checked: boolean) => {
    let newValue: string[]
    if (checked) {
      newValue = [...currentValue, optionValue]
    } else {
      newValue = currentValue.filter((v) => v !== optionValue)
    }
    if (!isControlled) setInternalValue(newValue)
    onChange?.(newValue)
  }

  return (
    <fieldset
      className={`m-0 border-0 p-0 ${className}`}
      role='group'
      aria-label={typeof label === 'string' ? label : undefined}
    >
      {label && <legend className='mb-2 text-label-default'>{label}</legend>}
      <div
        className={
          direction === 'vertical'
            ? 'flex flex-col gap-3'
            : 'flex flex-row flex-wrap items-center gap-6'
        }
      >
        {options.map((option) => (
          <CheckboxWithLabel
            key={option.value}
            checked={currentValue.includes(option.value)}
            disabled={disabled || option.disabled}
            aria-checked={option.indeterminate ? 'mixed' : currentValue.includes(option.value)}
            data-indeterminate={option.indeterminate ? true : undefined}
            onCheckedChange={(checked) => handleChange(option.value, !!checked)}
            className='text-label-default'
          >
            {option.label}
          </CheckboxWithLabel>
        ))}
      </div>
    </fieldset>
  )
}
